import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Divider,
  Card,
  CardContent,
  IconButton,
  Modal,
  TextField,
  Breadcrumbs,
  Link
} from '@mui/material';
import { 
  Download, 
  Chat, 
  CalendarToday, 
  DirectionsCar, 
  Business, 
  AttachMoney, 
  Share, 
  Home,
  Close 
} from '@mui/icons-material';
import AppLayout from '../components/layout/AppLayout';
import SupportButton from '../components/support/SupportButton';
import api from '../services/api';

interface ReportData {
  id: string;
  title: string;
  pdf_url: string;
  created_at: string;
  vehicle_info: any;
  dealership_name: string;
  file_size: number;
  report_content: {
    strategic_analysis: any;
    dealership_analysis_report: any;
    car_search_report: any;
  };
  metadata: {
    generation_date: string;
    report_type: string;
    version: string;
  };
}

const ReportPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) {
        setError('Report ID not provided');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/reports/${reportId}`);
        setReport(response.data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [reportId]);

  const handleDownloadPDF = () => {
    if (report?.pdf_url) {
      window.open(report.pdf_url, '_blank');
    }
  };

  const handleTellMeMore = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!report) {
      console.error('No report available');
      return;
    }

    try {
      const response = await api.post('/chat/report', {
        report_id: report.id
      });

      const chatId = response.data.id;
      
      if (!chatId) {
        throw new Error('No chat ID received from API');
      }
      
      navigate(`/streaming-chat/${chatId}`);

    } catch (err: any) {
      console.error('Failed to create chat:', err);
      console.error('Error details:', err.response?.data);
      
      if (err.response?.status === 402) {
        alert('You need coins to create a research chat. Creating a basic chat instead.');
      } else if (err.response?.status === 401) {
        alert('Please log in again to create a chat.');
        navigate('/login');
        return;
      }
      
      navigate('/chats');
    }
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const getShareUrl = () => {
    return window.location.href;
  };

  const getShareText = () => {
    if (!report) return '';
    const vehicleDisplay = getVehicleDisplay(report.vehicle_info);
    return `Check out my car analysis report for ${vehicleDisplay} from ${report.dealership_name || 'this dealership'}! 🚗`;
  };

  const handleSocialShare = (platform: string) => {
    const url = encodeURIComponent(getShareUrl());
    const text = encodeURIComponent(getShareText());
    
    let shareUrl = '';
    
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      case 'reddit':
        shareUrl = `https://reddit.com/submit?url=${url}&title=${text}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      const textArea = document.createElement('textarea');
      textArea.value = getShareUrl();
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };


  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getVehicleDisplay = (vehicleInfo: any) => {
    if (typeof vehicleInfo === 'string') return vehicleInfo;
    if (typeof vehicleInfo === 'object' && vehicleInfo) {
      const year = vehicleInfo.year || '';
      const make = vehicleInfo.make || '';
      const model = vehicleInfo.model || '';
      return `${year} ${make} ${model}`.trim() || 'Vehicle Information Available';
    }
    return 'Vehicle Information Available';
  };

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: 1200, mx: 'auto', textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Loading report...
          </Typography>
        </Box>
      </AppLayout>
    );
  }

  if (error || !report) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              textAlign: 'center', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography color="error" sx={{ mb: 2 }}>
              {error || 'Report not found'}
            </Typography>
            <Button onClick={() => navigate('/reports')} variant="outlined">
              Back to Reports
            </Button>
          </Paper>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/')}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5, fontSize: '1rem' }} />
            Home
          </Link>
          <Link 
            component="button" 
            variant="body1" 
            onClick={() => navigate('/reports')}
          >
            Reports
          </Link>
          <Typography color="text.primary">
            {getVehicleDisplay(report.vehicle_info)}
          </Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
          {/* Main content - left side */}
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 8' } }}>
            {/* Header */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                mb: 4, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h4" gutterBottom>
                {report.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CalendarToday sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body1" color="text.secondary">
                  {formatDate(report.created_at)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleTellMeMore}
                  startIcon={<Chat />}
                >
                  Ask Questions About This Report
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDownloadPDF}
                  startIcon={<Download />}
                >
                  Download PDF
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleShare}
                  startIcon={<Share />}
                >
                  Share
                </Button>
              </Box>
            </Paper>

            {/* Report Overview */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <DirectionsCar sx={{ fontSize: '2rem', color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Vehicle
                    </Typography>
                    <Typography variant="body1">
                      {getVehicleDisplay(report.vehicle_info)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Business sx={{ fontSize: '2rem', color: 'success.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Dealership
                    </Typography>
                    <Typography variant="body1">
                      {report.dealership_name || 'Information Available'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
                <Card sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <AttachMoney sx={{ fontSize: '2rem', color: 'secondary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>
                      Potential Savings
                    </Typography>
                    <Typography variant="body1">
                      Available in full report
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

          </Grid>

          {/* Right panel - sidebar */}
          <Grid sx={{ gridColumn: { xs: 'span 12', md: 'span 4' } }}>
            <Typography variant="h5" gutterBottom>
              Report Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Get Insights
              </Typography>
              <Button 
                variant="contained" 
                fullWidth 
                onClick={handleTellMeMore}
                startIcon={<Chat />}
                sx={{ mb: 2 }}
              >
                Ask Questions
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={handleDownloadPDF}
                startIcon={<Download />}
                sx={{ mb: 2 }}
              >
                Download PDF
              </Button>
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={handleShare}
                startIcon={<Share />}
              >
                Share Report
              </Button>
            </Paper>



            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Need Help?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Having issues with this report? Get support from our team.
              </Typography>
              <SupportButton
                context="report"
                reportId={report.id}
                variant="contained"
                fullWidth
                label="Get Support"
                showIcon={true}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Share Modal */}
        <Modal
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Paper 
            sx={{ 
              p: 4, 
              maxWidth: 400, 
              width: '90%',
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Share Your Car Report
              </Typography>
              <IconButton onClick={() => setShowShareModal(false)}>
                <Close />
              </IconButton>
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid sx={{ gridColumn: 'span 6' }}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => handleSocialShare('twitter')}
                  sx={{ bgcolor: '#1DA1F2', color: 'white', '&:hover': { bgcolor: '#1a8cd8' } }}
                >
                  🐦 Twitter
                </Button>
              </Grid>
              <Grid sx={{ gridColumn: 'span 6' }}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => handleSocialShare('facebook')}
                  sx={{ bgcolor: '#4267B2', color: 'white', '&:hover': { bgcolor: '#365899' } }}
                >
                  📘 Facebook
                </Button>
              </Grid>
              <Grid sx={{ gridColumn: 'span 6' }}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => handleSocialShare('linkedin')}
                  sx={{ bgcolor: '#0077B5', color: 'white', '&:hover': { bgcolor: '#005885' } }}
                >
                  💼 LinkedIn
                </Button>
              </Grid>
              <Grid sx={{ gridColumn: 'span 6' }}>
                <Button 
                  variant="outlined" 
                  fullWidth 
                  onClick={() => handleSocialShare('reddit')}
                  sx={{ bgcolor: '#FF4500', color: 'white', '&:hover': { bgcolor: '#e03d00' } }}
                >
                  🤖 Reddit
                </Button>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField 
                fullWidth
                size="small"
                value={getShareUrl()} 
                InputProps={{ readOnly: true }}
              />
              <Button 
                variant={copySuccess ? "contained" : "outlined"}
                onClick={handleCopyLink}
                color={copySuccess ? "success" : "primary"}
              >
                {copySuccess ? '✓ Copied' : 'Copy'}
              </Button>
            </Box>
          </Paper>
        </Modal>
      </Box>
    </AppLayout>
  );
};

export default ReportPage;